<template>
  <div>

        <section >
            <div class="">
                <!-- Mobile action buttons (always visible) -->
                <div class="d-flex d-md-none gap-2 mb-3" v-if="!this.isDetailClient">
                    <template v-if="helper.checkAccess('create_quote') != -1">
                        <button @click="create" class="btn btn-sm btn-success flex-grow-1">
                            <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                            <span>{{$t('quote')}}</span>
                        </button>
                    </template>
                    <div v-if="role === 'owner' || role === 'admin'" class="dropdown">
                        <button class="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center" type="button" data-bs-toggle="dropdown" aria-expanded="false" style="height: 31px; width: 34px; padding: 0;">
                            <AppIcon name="bi-three-dots-vertical" class="bi bi-three-dots-vertical" style="font-size:14px" />
                        </button>
                        <ul class="dropdown-menu dropdown-menu-end shadow">
                            <li>
                                <a class="dropdown-item d-flex align-items-center" href="#" @click.prevent="toggleTrashView">
                                    <AppIcon name="bi-trash" class="bi bi-trash me-2 text-danger" />
                                    <span>{{ $t('trash') }}</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <!-- Filters section (collapsible on mobile, always visible on desktop) -->
                <div class="row align-items-end g-2 mb-4" v-if="!this.isDetailClient" :class="{'d-none d-md-flex': !showFilters}">
                    
                    <!-- Filter Dropdown (Shopify Style) -->
                    <div class="col-12 col-md-auto">
                        <div class="dropdown">
                            <button class="btn btn-outline-secondary dropdown-toggle btn-sm" type="button" id="quoteFilterDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                                <AppIcon name="bi-funnel" class="bi bi-funnel" /> {{$t('filter')}}
                                <span v-if="activeFilters.length" class="badge bg-secondary ms-1">{{activeFilters.length}}</span>
                            </button>
                            <button v-if="activeFilters.length" class="btn btn-link btn-sm text-danger text-decoration-none ms-2" type="button" @click.stop="clearAllFilters">
                                <AppIcon name="bi-x-circle" class="bi bi-x-circle me-1" />{{$t('clear_all')}}
                            </button>
                            <div class="dropdown-menu p-3" aria-labelledby="quoteFilterDropdown" style="min-width: 300px;" @click.stop>
                                
                                <!-- Active Filters -->
                                <div v-if="activeFilters.length > 0" class="mb-3">
                                    <div v-for="(filter, index) in activeFilters" :key="index" class="mb-2">
                                        <label class="form-label small mb-1">{{$t(filter.key)}}</label>
                                        <div class="input-group input-group-sm" v-if="filter.key === 'total_amount'">
                                            <select v-model="filter.operator" class="form-select" style="max-width: 80px;">
                                                <option value="gt">{{$t('operator_gt')}}</option>
                                                <option value="lt">{{$t('operator_lt')}}</option>
                                                <option value="eq">{{$t('operator_eq')}}</option>
                                            </select>
                                            <input type="number" v-model="filter.value" class="form-control form-control-sm" placeholder="0">
                                            <button class="btn btn-outline-danger" type="button" @click="removeFilter(index)">
                                                <AppIcon name="bi-trash" class="bi bi-trash" />
                                            </button>
                                        </div>
                                        <div v-else-if="filter.key === 'client'" class="d-flex align-items-center gap-1">
                                            <div class="flex-grow-1">
                                                <auto-complete
                                                    entity="clients"
                                                    emitEvent="emitAutoCompleteClientFilter"
                                                    :defaultValue="clientFilterName"
                                                    @emitAutoCompleteClientFilter="emitAutoCompleteClientFilter"
                                                ></auto-complete>
                                            </div>
                                            <button class="btn btn-outline-danger btn-sm" type="button" @click="removeFilter(index)">
                                                <AppIcon name="bi-trash" class="bi bi-trash" />
                                            </button>
                                        </div>
                                        <div class="input-group input-group-sm" v-else>
                                            <select v-model="filter.value" class="form-select form-select-sm">
                                                <option v-for="option in getFilterOptions(filter.key)" :key="option.value" :value="option.value">
                                                    {{$t(option.label)}}
                                                </option>
                                            </select>
                                            <button class="btn btn-outline-danger" type="button" @click="removeFilter(index)">
                                                <AppIcon name="bi-trash" class="bi bi-trash" />
                                            </button>
                                        </div>
                                    </div>
                                    <hr class="dropdown-divider">
                                </div>

                                <!-- Add Filter Options -->
                                <div class="d-grid gap-2">
                                    <button v-for="filter in selectableFilters" :key="filter.key" 
                                            class="btn btn-sm btn-outline-primary text-start" 
                                            @click="addFilter(filter)">
                                        <AppIcon name="bi-plus" class="bi bi-plus" /> {{$t(filter.key)}}
                                    </button>
                                </div>

                                <!-- Apply and Clear -->
                                <div class="d-flex justify-content-between align-items-center mt-3">
                                    <button class="btn btn-sm btn-success" @click="applyFilters">
                                        {{$t('apply')}}
                                    </button>
                                    <button v-if="activeFilters.length > 0" class="btn btn-link btn-sm text-decoration-none text-danger p-0" @click="clearAllFilters">
                                        {{$t('clear_all')}}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Spacer for desktop -->
                    <div class="col d-none d-md-block"></div>
                    
                    <!-- Date picker -->
                    <div class="col-12 col-md-3 col-lg-2">
                        <label class="form-label small d-md-none mb-1">{{$t('filter_by_date')}}</label>
                        <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select form-select-sm">
                            <option value="">{{$t('filter_by_date')}}</option>
                            <option value="today">{{$t('today')}}</option>
                            <option value="yesterday">{{$t('yesterday')}}</option>
                            <option value="this_week">{{$t('this_week')}}</option>
                            <option value="last_week">{{$t('last_week')}}</option>
                            <option value="this_month">{{$t('this_month')}}</option>
                            <option value="custom">{{$t('custom_date')}}</option>
                        </select>
                    </div>
                    <div class="col-12 col-md-3 col-lg-2" v-if="dateRangeOption === 'custom'">
                        <label class="form-label small d-md-none mb-1">{{$t('custom_date')}}</label>
                        <date-picker class="w-100" :clearable="true" :editable="false" v-model="range" type="date" range :multi-calendars="1" :placeholder="$t('select_date_range')"></date-picker>
                    </div>
                    
                    <!-- Desktop action buttons -->
                    <template v-if="helper.checkAccess('create_quote') != -1">
                        <div class="col-md-2 d-none d-md-block">
                            <button @click="create" class="btn btn-sm btn-success w-100">
                                <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                                <span>{{$t('quote')}}</span>
                            </button>
                        </div>
                    </template>
                    
                </div>
                <div class="page-table-container mb-6 bg-white rounded shadow">

                    <!-- table -->
                    <div v-if="!showTrash" data-testid="quote-table-scroll" class="table-responsive document-list-table">
                        <table class="table mb-0 table-borderless table-striped small sales-document-table" style="--sales-document-table-min-width: 1100px;">
                            <thead>
                                <tr class="text-secondary">
                                    <th class="pt-4 pb-3 px-6" style="width: 40px;">
                                        <div class="form-check mb-0">
                                            <input class="form-check-input" type="checkbox" :checked="areAllSelected" @change="toggleSelectAll">
                                        </div>
                                    </th>
                                    <sortable-table-header v-if="isDocumentColumnVisible('reference')" field="reference" label="reference" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getQuote)" />
                                    <sortable-table-header v-if="isDocumentColumnVisible('client') && !hideClient" field="client" label="client" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getQuote)" />
                                    <th v-if="isDocumentColumnVisible('total_products')" class="pt-4 pb-3 px-6">{{$t('total_products')}}</th>
                                    <sortable-table-header v-if="isDocumentColumnVisible('total')" field="total" label="total" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getQuote)" />
                                    <sortable-table-header v-if="isDocumentColumnVisible('date')" field="date" label="date" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getQuote)" />
                                    <sortable-table-header v-if="isDocumentColumnVisible('status')" field="status" label="status" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getQuote)" />
                                    <sortable-table-header v-if="isDocumentColumnVisible('created_by')" field="created_by" label="created_by" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getQuote)" />
                                    <th v-if="isDocumentColumnVisible('action')" class="pt-4 pb-3 px-6 sales-sticky-action-cell">
                                        <div class="d-flex align-items-center justify-content-between">
                                            <span>{{$t('action')}}</span>
                                            <div v-if="role === 'owner' || role === 'admin'" class="dropdown">
                                                <button class="btn btn-sm btn-link p-0 ms-2 text-secondary" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                                    <AppIcon name="bi-three-dots-vertical" class="bi bi-three-dots-vertical" />
                                                </button>
                                                <ul class="dropdown-menu dropdown-menu-end shadow">
                                                    <li>
                                                        <a class="dropdown-item d-flex align-items-center" href="#" @click.prevent="toggleTrashView">
                                                            <AppIcon name="bi-trash" class="bi bi-trash me-2 text-danger" />
                                                            <span>{{ $t('trash') }}</span>
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                            <tr v-for="item in quote" :key="item.id" :class="{'table-active': item.selected}">
                                <td class="py-5 px-6">
                                    <div class="form-check mb-0">
                                        <input class="form-check-input" type="checkbox" v-model="item.selected" @change="changeSelectedItem">
                                    </div>
                                </td>
                                <td v-if="isDocumentColumnVisible('reference')" class="py-5 px-6"><router-link :to="`/quotes/${item.id}`"><b>{{item.reference}}</b></router-link></td>
                                <td v-if="isDocumentColumnVisible('client') && !hideClient" class="py-5 px-6">
                                    <router-link
                                        v-if="item.client && item.client.id"
                                        :to="`/clients/${item.client.id}`"
                                        :title="item.client.name || '-'"
                                        :aria-label="item.client.name || '-'"
                                    >{{truncateClientName(item.client.name)}}</router-link>
                                </td>
                                <td v-if="isDocumentColumnVisible('total_products')" class="py-5 px-6">{{item.products.length}}</td>
                                <td v-if="isDocumentColumnVisible('total')" class="py-5 px-6">{{formatMoney(calculateItemPrice(item))}} </td>
                                <td v-if="isDocumentColumnVisible('date')" class="py-5 px-6">{{formatDate(item.date)}}</td>
                                <td v-if="isDocumentColumnVisible('status')" class="py-5 px-6">
                                    <div v-if="helper.checkAccess('edit_quote') != -1 && !isQuoteStatusDropdownDisabled(item)" class="dropdown">
                                        <button
                                            :id="`quote-status-${item.id}`"
                                            type="button"
                                            class="badge border-0 dropdown-toggle"
                                            :class="getQuoteStatusBadgeClass(item.status)"
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                            :disabled="isQuoteStatusDropdownDisabled(item)"
                                        >
                                            <span v-if="quoteStatusUpdatingId === item.id" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            {{ $t(item.status) }}
                                        </button>
                                        <ul class="dropdown-menu shadow" :aria-labelledby="`quote-status-${item.id}`">
                                            <li v-for="status in getQuoteStatusOptions(item)" :key="status">
                                                <button
                                                    type="button"
                                                    class="dropdown-item d-flex align-items-center justify-content-between gap-3"
                                                    :class="{ active: item.status === status }"
                                                    :disabled="item.status === status"
                                                    @click="changeQuoteStatus(item, status)"
                                                >
                                                    <span>{{ $t(status) }}</span>
                                                    <AppIcon name="bi-check-lg" v-if="item.status === status" class="bi bi-check-lg" />
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                    <span v-else class="badge" :class="getQuoteStatusBadgeClass(item.status)">
                                        {{ $t(item.status) }}
                                    </span>
                                </td>
                                <td v-if="isDocumentColumnVisible('created_by')" class="py-5 px-6">{{item.user ? item.user.name : '-'}}</td>
                                <td v-if="isDocumentColumnVisible('action')" class="py-5 px-6 sales-sticky-action-cell">
                                    <div class="sales-row-actions">
                                        <div v-if="!['draft', 'canceled'].includes(item.status) && (helper.checkAccess('create_orders') > 0 || helper.checkAccess('create_deliveries') > 0 || helper.checkAccess('create_invoices') > 0)" class="dropdown d-inline-block">
                                            <button class="btn btn-primary btn-xs dropdown-toggle" type="button" :id="`quote-transfer-${item.id}`" data-bs-toggle="dropdown" aria-expanded="false">
                                                <AppIcon name="bi-arrow-left-right" class="bi bi-arrow-left-right" style="font-size:12px" /> {{$t('transfert')}}
                                            </button>
                                            <ul class="dropdown-menu dropdown-menu-end" :aria-labelledby="`quote-transfer-${item.id}`">
                                                <li v-if="helper.checkAccess('create_orders') > 0"><a class="dropdown-item" style="cursor:pointer" @click="quoteAccepted(item)"> {{$t('order')}}</a></li>
                                                <li v-if="helper.checkAccess('create_deliveries') > 0"><a class="dropdown-item" style="cursor:pointer" @click="transferDelivery(item)"> {{$t('delivery')}}</a></li>
                                                <li v-if="helper.checkAccess('create_invoices') > 0"><a class="dropdown-item" style="cursor:pointer" @click="transferInvoice(item)"> {{$t('invoice')}}</a></li>
                                            </ul>
                                        </div>
                                        <button v-if="item.status != 'draft'" @click="viewRow(item)" class="btn btn-primary btn-xs quote-action-button">
                                           <AppIcon name="bi-eye" class="bi bi-eye" style="font-size:12px" />
                                            {{$t('quote')}}
                                        </button>
                                        <button v-if="helper.checkAccess('edit_quote') != -1" @click="editRow(item)" class="btn btn-primary btn-xs quote-action-button">
                                            <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
                                            {{$t('edit')}}
                                        </button>
                                        <button v-if="helper.checkAccess('delete_quote') != -1" @click="deleteRow(item)" class="btn btn-primary btn-xs quote-action-button">
                                            <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
                                            {{$t('delete')}}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="quote.length" class="sales-total-row" style="background-color: #f8f9fa;font-weight: bold">
                                <td>{{$t('total')}}:</td>
                                <td v-if="isDocumentColumnVisible('reference')"></td>
                                <td v-if="isDocumentColumnVisible('client') && !hideClient"></td>
                                <td v-if="isDocumentColumnVisible('total_products')"></td>
                                <td v-if="isDocumentColumnVisible('total')" class="py-5 px-6">{{formatMoney(quote.reduce((acc, item) => acc + calculateItemPrice(item), 0))}} </td>
                                <td v-if="isDocumentColumnVisible('date')"></td>
                                <td v-if="isDocumentColumnVisible('status')"></td>
                                <td v-if="isDocumentColumnVisible('created_by')"></td>
                                <td v-if="isDocumentColumnVisible('action')" class="sales-sticky-action-cell"></td>
                            </tr>
                            </tbody>
                        </table>

                        <div v-if="count > quote.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
                            <compact-pagination :pages="pagination" :current-page="page" @change="getQuote" />
                        </div>
                        <table-loading-bar v-if="loading" class="page-table-state document-list-loading" />
                    </div>
                    <!-- table -->
                    <div v-if="!showTrash && loading === false && !quote.length && !isFiltered" class="page-table-state document-list-empty text-center my-5 bg-white rounded shadow-sm border" style="border-style: dashed !important;">
                        <AppIcon name="bi-file-earmark-text" class="bi bi-file-earmark-text text-muted" style="font-size: 3rem;" />
                        <h4 class="mt-3">{{ $t('no_quotes_added') }}</h4>
                        <p class="text-muted">{{ $t('start_by_adding_quotes') }}</p>
                        <template v-if="helper.checkAccess('create_quote') != -1">
                            <button class="btn btn-outline-primary mt-2" @click="create">
                                {{ $t('create_first_quote') }}
                            </button>
                        </template>
                    </div>
                    <div v-if="!showTrash && loading === false && !quote.length && isFiltered" class="page-table-state document-list-empty text-center">{{$t('no_quote_found')}}</div>

                    <div v-if="showTrash && isOwner" class="mt-5">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6 class="mb-0"><AppIcon name="bi-trash" class="bi bi-trash me-2" />{{$t('trash')}}</h6>
                            <a href="#" class="text-decoration-none small d-flex align-items-center text-secondary" @click.prevent="toggleTrashView">
                                <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-1" />{{$t('back')}}
                            </a>
                        </div>
                        <table-loading-bar v-if="loadingTrash" class="text-center" />
                        <div v-if="!loadingTrash" class="table-responsive">
                            <table class="table mb-0 table-borderless table-striped small">
                                <thead>
                                    <tr class="text-secondary">
                                        <th class="pt-4 pb-3 px-6">{{$t('reference')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('client')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('date')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('total')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('deleted_at')}}</th>
                                        <th class="pt-4 pb-3 px-6">{{$t('actions')}}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in trashQuotes" :key="item.id">
                                        <td class="py-5 px-6">{{item.reference}}</td>
                                        <td class="py-5 px-6">{{item.client ? item.client.name : '-'}}</td>
                                        <td class="py-5 px-6">{{formatDate(item.date)}}</td>
                                        <td class="py-5 px-6">{{formatMoney(item.total)}}</td>
                                        <td class="py-5 px-6">{{formatDate(item.deleted_at, true)}}</td>
                                        <td class="py-5 px-6">
                                            <button @click="viewRow(item)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                                                <AppIcon name="bi-eye" class="bi bi-eye" style="font-size:12px" />
                                                {{$t('view')}}
                                            </button>
                                            <button v-if="helper.checkAccess('delete_quote') != -1" @click="permanentDeleteQuote(item)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-danger btn-xs">
                                                <AppIcon name="bi-x-circle" class="bi bi-x-circle" style="font-size:12px" />
                                                {{$t('delete_permanently')}}
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-if="!loadingTrash && trashQuotes.length === 0" class="text-center py-4 text-muted">
                            <AppIcon name="bi-trash" class="bi bi-trash text-muted" style="font-size: 2rem;" />
                            <p class="mt-2 mb-0">{{$t('trash_empty')}}</p>
                        </div>
                        <div v-if="trashCount > trashQuotes.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
                            <compact-pagination :pages="trashPagination" :current-page="trashPage" @change="getTrashQuotes" />
                        </div>
                    </div>

                </div>
                <div v-if="!showTrash" class="col-sm-7">
                    {{$t('show')}}
                    <select v-model="per_page" class="form-select mb-3" name="Resulats" style="cursor:pointer;margin-right: 5px;width:100px;display:inline-block">
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="15">15</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    {{$t('entities')}}
                </div>
            </div>
        </section>

        <!-- Bulk Action Bar -->
        <div v-if="isSelectedItem" class="position-fixed bottom-0 start-50 translate-middle-x mb-4 p-3 bg-white shadow rounded d-flex align-items-center gap-3" style="z-index: 1050; border: 1px solid #dee2e6; min-width: 300px; justify-content: space-between;">
            <div class="d-flex align-items-center">
                <span class="fw-bold me-2">{{ selectedCount }}</span>
                <span class="text-muted">{{$t('selected')}}</span>
            </div>
            <div class="d-flex gap-2">
                <div class="dropdown dropup" v-if="helper.checkAccess('create_orders') > 0 || helper.checkAccess('create_deliveries') > 0 || helper.checkAccess('create_invoices') > 0">
                    <button class="btn btn-primary btn-sm dropdown-toggle d-flex align-items-center" type="button" id="bulkTransferDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                        <AppIcon name="bi-arrow-left-right" class="bi bi-arrow-left-right me-1" /> {{$t('transfert')}}
                    </button>
                    <ul class="dropdown-menu" aria-labelledby="bulkTransferDropdown">
                        <li v-if="helper.checkAccess('create_orders') > 0"><a class="dropdown-item" style="cursor:pointer" @click="transferSelectedToOrder"> {{$t('order')}}</a></li>
                        <li v-if="helper.checkAccess('create_deliveries') > 0"><a class="dropdown-item" style="cursor:pointer" @click="transferSelectedToDelivery"> {{$t('delivery')}}</a></li>
                        <li v-if="helper.checkAccess('create_invoices') > 0"><a class="dropdown-item" style="cursor:pointer" @click="transferSelectedToInvoice"> {{$t('invoice')}}</a></li>
                    </ul>
                </div>
                <button v-if="helper.checkAccess('delete_quote') != -1" class="btn btn-danger btn-sm d-flex align-items-center" @click="deleteSelected">
                    <AppIcon name="bi-trash" class="bi bi-trash me-1" /> {{$t('delete')}}
                </button>
            </div>
        </div>


        <!-- create Modal -->
        <add-edit-quote-modal 
            v-if="createModalisOpen" 
            @modalClosed="createModalisOpen = false"
            modalId="create-quote-modal-012" 
            :isModalOpen="createModalisOpen" 
            :data="item" 
            action="create"
            @refresh="refresh"
        ></add-edit-quote-modal>
        <!-- create modal -->

        <!-- Edit Modal -->
        <add-edit-quote-modal 
            v-if="editModalisOpen" 
            @modalClosed="editModalisOpen = false" 
            modalId="edit-quote-modal-01" 
            :isModalOpen="editModalisOpen"  
            :data="item" 
            action="update" 
            @refresh="refresh"
        ></add-edit-quote-modal>
        <!-- Edit modal -->

        <!-- quote modal -->
        <invoice-template-modal 
            v-if="invoiceModalisOpen" 
            @modalClosed="invoiceModalisOpen = false" 
            modalId="invoice-quote-modal-01" 
            :isModalOpen="invoiceModalisOpen"  
            :data="item" 
            type="quote">
        </invoice-template-modal>
        <!-- quote modal -->
        
        <!-- create Modal -->
        <add-edit-invoice-modal 
            v-if="createInvoiceModalState" 
            @modalClosed="createInvoiceModalState = false"
            modalId="create-invoice-modal-01" 
            :isModalOpen="createInvoiceModalState" 
            :data="item" 
            action="transfer"
        ></add-edit-invoice-modal>
        <!-- create modal -->

        <!-- create Modal -->
        <add-edit-delivery-modal 
            v-if="createDeliveryModalState" 
            @modalClosed="createDeliveryModalState = false"
            modalId="create-delivery-modal-01" 
            :isModalOpen="createDeliveryModalState" 
            :data="item" 
            action="transfer"
        ></add-edit-delivery-modal>
        <!-- create modal -->

        <!-- create Modal -->
        <add-edit-order-modal 
            v-if="createOrderModalState" 
            @modalClosed="createOrderModalState = false"
            modalId="create-order-modal-01" 
            :isModalOpen="createOrderModalState" 
            :data="item" 
            action="transfer"
        ></add-edit-order-modal>
        <!-- create modal -->

    <div>
    </div>

  </div>
</template>

<script>
import helper from '../helper'
import moment from 'moment'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

import AddEditQuoteModal from './quote/AddEditQuoteModal'
import AddEditInvoiceModal from './invoice/AddEditInvoiceModal';
import AddEditDeliveryModal from './delivery/AddEditDeliveryModal';
import AddEditOrderModal from './order/AddEditOrderModal';

import InvoiceTemplateModal from './invoiceLayout/InvoiceTemplateModal';
import AutoComplete from './autocomplete/AutoComplete';
import sortableList from '../mixins/sortableList';
import { getSalesDocumentColumnDefaults, getSalesDocumentColumnMetadataKey } from '../constants/salesDocumentColumns';

export default {
  name: 'Quote',
  persistedFilterState: {
    key: 'quotes',
    fields: ['activeFilters', 'clientFilterName', 'dateRangeOption', 'range', 'start', 'end', 'per_page', 'sortField', 'sortDirection'],
    dateFields: ['range'],
  },
  mixins: [sortableList],
  props : [
    'openCreateModal',
    'client_id',
    'client',
    'product_id',
    'user_id',
    'inventory_id',
    'keyword',
    'isDetailClient',
    'hideAction',
    'quote_id',
    'showFilters'
 ],
  components : {
    InvoiceTemplateModal,
    DatePicker,
    AddEditQuoteModal,
    AddEditInvoiceModal,
    AddEditDeliveryModal,
    AddEditOrderModal,
    AutoComplete
  },
  data(){
    return {
        quote : [],
        count : 0,
        page : 1,
        item : {
            id : null,
            client_id : null,
            client : {},
            reference : `Quote-00`,
            date : '',
            notes : '',
            total : 0,
            products : []
        },
        range: [],
        start : null,
        end : null,
        dateRangeOption: '',
        activeFilters: [],
        availableFilters: [
            { 
                key: 'status', 
                options: [
                    { value: 'draft', label: 'draft' },
                    { value: 'pending', label: 'pending' },
                    { value: 'confirmed', label: 'confirmed' },
                    { value: 'canceled', label: 'canceled' }
                ] 
            },
            {
                key: 'client',
                options: []
            },
            {
                key: 'total_amount',
                options: []
            }
        ],
        quoteProducts : [],
        createModal : null,
        editModal : null,
        loading : false,
        invoiceModalisOpen :false,
        per_page : 25,
        pagination : [],
        editModalisOpen :false,
        createModalisOpen : false,
        hideClient : false,
        createInvoiceModalState : false,
        createDeliveryModalState : false,
        createOrderModalState : false,
        quoteStatusUpdatingId: null,
        quoteStatusOptions: ['pending', 'confirmed', 'canceled'],
        isSelectedItem: false,
        showTrash: false,
        trashQuotes: [],
        trashCount: 0,
        trashPage: 1,
        trashPagination: [],
        loadingTrash: false,
        clientFilterName: ''
    }
  },
  mounted(){
    //check permissions
    if(helper.checkAccess('view_quotes') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_quotes') == -2) return this.$router.push({path : '/subscribe'})
    this.handleDateRangeChange()
    if(this.isOwner) this.getTrashCount()
  },
  watch : {
    range(val) {
        if (val && val.length === 2 && val[0] && val[1]) {
            this.start = moment(val[0]).format('YYYY-MM-DD')
            this.end = moment(val[1]).format('YYYY-MM-DD')
            this.getQuote(1)
        } else {
            this.start = null
            this.end = null
            this.getQuote(1)
        }
    },
    per_page(){
        this.getQuote(this.page)
    },
    openCreateModal(){
        this.createModalStatus = this.openCreateModal
        this.create()
    },
    keyword(){
        if(this.keyword.length > 2 || this.keyword === ''){
            this.getQuote(1)
        }
    },
    createModalisOpen(){
        this.getQuote()
    }
  },    
  computed : {
    isOwner(){ return ['owner', 'admin'].includes(this.$store.getters.ROLE)},
    role(){ return this.$store.getters.ROLE },
    selectableFilters() {
        return this.availableFilters.filter(f => !this.isFilterActive(f.key));
    },
    isFiltered() {
      return (this.activeFilters.length > 0) ||
             (this.dateRangeOption && this.dateRangeOption !== '') ||
             (this.keyword && this.keyword.length > 0) ||
             (this.range && this.range.length > 0);
    },
    business(){ return this.$store.getters.BUSINESS},
    documentColumns() {
      return {
        ...getSalesDocumentColumnDefaults('quote'),
        ...(this.business?.metadata?.[getSalesDocumentColumnMetadataKey('quote')] || {})
      }
    },
    helper(){ return helper },
    selectedCount() {
      return this.quote.filter(item => item.selected).length;
    },
    areAllSelected() {
      return this.quote.length > 0 && this.quote.every(item => item.selected);
    }
  },
  methods : {
    isDocumentColumnVisible(key) {
      return this.documentColumns[key] !== false
    },
    truncateClientName(name) {
      if (!name) return '-'
      return name.length > 20 ? `${name.slice(0, 20)}...` : name
    },
    async create(){ 

        this.clearForm()
        
        if(this.client_id){
            this.item.client_id = this.client_id
        }

        if(this.client){
            this.item.client = this.client
        }

        this.item.date = moment().format('YYYY-MM-DD')
        this.item.products.push({
            label : '',
            id : null,
            quantity : 0,
            price : 0,
            discount : 0,
            vat : this.business.vat,
            total : 0,
            sub_total : 0,
        })
        this.createModalisOpen = true

    },
    editRow(row){
        this.clearForm()
        this.item = row 
        this.editModalisOpen = true
    },
    viewRow(row){
        this.clearForm()
        this.item = row 
        this.invoiceModalisOpen = true
    },
    async deleteRow(row){
        this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text : this.$t('item_moved_to_trash'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
        }).then(async (result) => {
            if (result.isConfirmed) {
                await helper.customDelete(`${process.env.VUE_APP_API}/quotes/${row.id}`)
                this.quote = helper.withoutDeletedRecords(this.quote, row.id)
                this.count = Math.max(0, this.count - 1)
                this.getTrashCount()
            }
        })
    },
    clearForm(){
        this.item = {
            id : null,
            reference : '',
            client_id : null,
            client : {},
            date : '',
            total : 0,
            products : []
        }
    },
    async getQuote(page){
        try{
            this.loading = true
            this.page = page ? page : 1
            
            // Extract filters
            const statusFilter = this.activeFilters.find(f => f.key === 'status');
            const status = statusFilter ? statusFilter.value : 'all';

            const clientFilter = this.activeFilters.find(f => f.key === 'client');
            const amountFilter = this.activeFilters.find(f => f.key === 'total_amount');

            // Build query params
            const params = new URLSearchParams();
            params.append('page', this.page);
            params.append('per_page', this.per_page);
            this.appendSortParams(params);

            if (this.start) {
                params.append('start', this.start);
                params.append('end', this.end);
            }

            if (status !== 'all') params.append('status', status);
            if (clientFilter && clientFilter.value) params.append('client_id', clientFilter.value);
            
            if (amountFilter && amountFilter.value !== null && amountFilter.value !== '') {
                params.append('amount_operator', amountFilter.operator);
                params.append('amount_value', amountFilter.value);
            }
            
            // Add optional filters if they exist
            if (this.client_id) params.append('client_id', this.client_id);
            if (this.inventory_id) params.append('inventory_id', this.inventory_id);
            if (this.product_id) params.append('product_id', this.product_id);
            if (this.user_id) params.append('user_id', this.user_id);
            if (this.quote_id) params.append('quote_id', this.quote_id);
            if (this.keyword && this.keyword.length > 2) params.append('keyword', this.keyword);

            let url = `${process.env.VUE_APP_API}/quotes?${params.toString()}`;

            const response = await helper.customGet(url)
            this.quote = response.data.data

            for(let i = 0 ; i < this.quote.length ; i++){
                this.$set(this.quote[i], 'selected', false)
            }

            this.count = response.data.count
            if(this.per_page != response.data.per_page){
                this.per_page = response.data.per_page
            }

            //pagination
            this.pagination = []
            let MaxPagination = Math.ceil(this.count / this.per_page )
            for(let i = 1 ; i <= MaxPagination ;i++){
                this.pagination.push(i)
            }

            this.loading = false
        }catch(error){
            this.loading = false
        }

    },
    getQuoteStatusBadgeClass(status){
        return {
            'bg-info': status === 'draft',
            'bg-success': status === 'confirmed',
            'bg-danger': status === 'canceled',
            'bg-secondary': status === 'pending'
        }
    },
    getQuoteStatusOptions(item){
        return item.status === 'confirmed' ? ['canceled'] : this.quoteStatusOptions
    },
    isQuoteStatusDropdownDisabled(item){
        return this.quoteStatusUpdatingId === item.id
            || ['draft', 'canceled'].includes(item.status)
    },
    async changeQuoteStatus(item, status){
        if (this.isQuoteStatusDropdownDisabled(item) || item.status === status) return

        const previousStatus = item.status
        this.quoteStatusUpdatingId = item.id
        this.$set(item, 'status', status)

        try{
            await helper.customPatch(
                `${process.env.VUE_APP_API}/quotes/${item.id}`,
                { status },
                { showToast: true }
            )
        }catch(error){
            this.$set(item, 'status', previousStatus)
        }finally{
            await this.getQuote(this.page)
            this.quoteStatusUpdatingId = null
        }
    },
    calculateItemPrice(item){
        if(item.status == "draft") return 0
        return item.products && item.products.length ? item.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0; 
    },
    async quoteAccepted(quote){
 
        quote.total = quote.products && quote.products.length ? quote.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;
        quote.quote_id = quote.id
        this.item = quote
        this.createOrderModalState = !this.createOrderModalState

    },
    async transferInvoice(quote){
        quote.total = quote.products && quote.products.length ? quote.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;
        quote.quote_id = quote.id
        this.item = quote
        this.createInvoiceModalState = !this.createInvoiceModalState

    }, 
    async transferDelivery(quote){

        quote.total = quote.products && quote.products.length ? quote.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;
        quote.quote_id = quote.id
        this.item = quote
        this.createDeliveryModalState = !this.createDeliveryModalState

    },
    createInvoiceModal(data){
        this.createInvoiceModalState = data
    },
    refresh(){
        this.getQuote()
    },
    handleDateRangeChange() {
      const option = this.dateRangeOption
      if (!option || option === 'custom') {
        if (option === 'custom') {
          // Wait for user to select custom dates
          return
        }
        // Clear filter
        this.range = []
        this.start = null
        this.end = null
        this.getQuote()
        return
      }

      const today = moment()
      let start, end

      switch(option) {
        case 'today':
          start = today.clone().startOf('day')
          end = today.clone().endOf('day')
          break
        case 'yesterday':
          start = today.clone().subtract(1, 'days').startOf('day')
          end = today.clone().subtract(1, 'days').endOf('day')
          break
        case 'this_week':
          start = today.clone().startOf('week')
          end = today.clone().endOf('week')
          break
        case 'last_week':
          start = today.clone().subtract(1, 'weeks').startOf('week')
          end = today.clone().subtract(1, 'weeks').endOf('week')
          break
        case 'this_month':
          start = today.clone().startOf('month')
          end = today.clone().endOf('month')
          break
        default:
          return
      }

      this.range = [start.toDate(), end.toDate()]
      this.start = start.format('YYYY-MM-DD')
      this.end = end.format('YYYY-MM-DD')
      this.getQuote()
    },
    //helper functions
    formatDate(date,locale) { return helper.formatDate(date,locale) },
    formatMoney(number) { return helper.formatMoney(number);},
    formatReference(number,id) { return helper.formatReference(number,id);},
    addFilter(filter) {
        if (!this.isFilterActive(filter.key)) {
            if (filter.key === 'total_amount') {
                 this.activeFilters.push({
                    key: filter.key,
                    operator: 'gt',
                    value: 0
                });
            } else if (filter.key === 'client') {
                this.clientFilterName = '';
                this.activeFilters.push({
                    key: filter.key,
                    value: null
                });
            } else {
                const defaultVal = filter.options[0].value;
                this.activeFilters.push({ 
                    key: filter.key, 
                    value: defaultVal 
                });
            }
        }
    },
    removeFilter(index) {
        const filter = this.activeFilters[index];
        if (filter && filter.key === 'client') {
            this.clientFilterName = '';
        }
        this.activeFilters.splice(index, 1);
    },
    isFilterActive(key) {
        return this.activeFilters.some(f => f.key === key);
    },
    getFilterOptions(key) {
        const filter = this.availableFilters.find(f => f.key === key);
        return filter ? filter.options : [];
    },
    applyFilters() {
        this.getQuote();
    },
    clearAllFilters() {
        this.activeFilters = [];
        this.clientFilterName = '';
        this.getQuote();
    },
    emitAutoCompleteClientFilter(data) {
        const clientFilter = this.activeFilters.find(f => f.key === 'client');
        if (data.clear_input) {
            if (clientFilter) {
                clientFilter.value = null;
            }
            this.clientFilterName = '';
            return;
        }
        if (data.result && clientFilter) {
            clientFilter.value = data.result.id;
            this.clientFilterName = data.result.name;
        }
    },
    changeSelectedItem(){
        this.isSelectedItem = this.quote.find(item => item.selected === true ) ? true : false
    },
    toggleSelectAll() {
      const newState = !this.areAllSelected;
      this.quote.forEach(item => item.selected = newState);
      this.changeSelectedItem();
    },
    async deleteSelected() {
      const selectedIds = this.quote.filter(item => item.selected).map(item => item.id);
      if (selectedIds.length === 0) return;

      this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      }).then(async (result) => {
        if (result.isConfirmed) {
            this.loading = true;
            try {
                const results = await Promise.allSettled(selectedIds.map(id => helper.customDelete(`${process.env.VUE_APP_API}/quotes/${id}`)));
                const deletedIds = helper.fulfilledRecordIds(selectedIds, results);
                this.quote = helper.withoutDeletedRecords(this.quote, deletedIds);
                this.count = Math.max(0, this.count - deletedIds.length);
                if (deletedIds.length !== selectedIds.length) throw results.find(result => result.status === 'rejected').reason;
                this.isSelectedItem = false;
                this.$toast.open({
                    message: this.$t('quotes_deleted_successfully'),
                    type: 'success',
                    position: 'bottom'
                });
            } catch (error) {
                this.$toast.open({
                    message: this.$t('error_occurred'),
                    type: 'error',
                    position: 'bottom'
                });
            } finally {
                this.loading = false;
            }
        }
      });
    },
    validateBulkTransfer() {
        const selectedQuotes = this.quote.filter(item => item.selected);
        if (selectedQuotes.length === 0) return null;

        const clientId = selectedQuotes[0].client_id;
        const allSameClient = selectedQuotes.every(q => q.client_id === clientId);

        if (!allSameClient) {
            this.$swal.fire(
                this.$t('error'),
                this.$t('select_quotes_same_client'),
                'error'
            );
            return null;
        }
        
        // Aggregate products
        let allProducts = [];
        selectedQuotes.forEach(q => {
            if (q.products && q.products.length) {
                allProducts = allProducts.concat(q.products);
            }
        });

        // Calculate total
        const total = allProducts.reduce((acc, p) => acc + (p.total || 0), 0);

        return {
            id: null,
            quote_ids: selectedQuotes.map(q => q.id),
            client_id: clientId,
            status : 'pending',
            client: selectedQuotes[0].client,
            date: moment().format('YYYY-MM-DD'),
            products: allProducts,
            total: total
        };
    },
    transferSelectedToInvoice() {
        const mergedData = this.validateBulkTransfer();
        if (!mergedData) return;

        this.item = mergedData;
        this.createInvoiceModalState = true;
    },
    transferSelectedToOrder() {
        const mergedData = this.validateBulkTransfer();
        if (!mergedData) return;

        this.item = mergedData;
        this.createOrderModalState = true;
    },
    transferSelectedToDelivery() {
        const mergedData = this.validateBulkTransfer();
        if (!mergedData) return;

        this.item = mergedData;
        this.createDeliveryModalState = true;
    },
    toggleTrashView() {
        this.showTrash = !this.showTrash;
        if (this.showTrash) {
            this.getTrashQuotes(1);
        }
    },
    async getTrashQuotes(page) {
        try {
            this.loadingTrash = true;
            this.trashPage = page || 1;
            const response = await helper.customGet(`${process.env.VUE_APP_API}/quotes/trash?page=${this.trashPage}&per_page=50`);
            this.trashQuotes = response.data.data;
            this.trashCount = response.data.count;
            this.trashPagination = [];
            const maxPages = Math.ceil(this.trashCount / 50);
            for (let i = 1; i <= maxPages; i++) {
                this.trashPagination.push(i);
            }
            this.loadingTrash = false;
        } catch (error) {
            this.loadingTrash = false;
        }
    },
    async getTrashCount() {
        try {
            const response = await helper.customGet(`${process.env.VUE_APP_API}/quotes/trash?page=1&per_page=1`);
            this.trashCount = response.data.count || 0;
        } catch (error) {
            // ignore
        }
    },
    async restoreQuote(item) {
        try {
            await helper.customPost(`${process.env.VUE_APP_API}/quotes/${item.id}/restore`);
            this.getTrashQuotes(this.trashPage);
            this.getQuote();
            this.getTrashCount();
            window.vueOpener.open({
                message: this.$t('restored_successfully'),
                type: 'success',
                position: 'bottom'
            });
        } catch (error) {
            helper.handleError(error);
        }
    },
    async permanentDeleteQuote(item) {
        this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text: this.$t('you_cant_revert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: this.$t('yes_delete_it')
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await helper.customDelete(`${process.env.VUE_APP_API}/quotes/${item.id}/permanent`);
                    this.trashQuotes = helper.withoutDeletedRecords(this.trashQuotes, item.id);
                    this.getTrashCount();
                } catch (error) {
                    helper.handleError(error);
                }
            }
        });
    },


  }
}
</script>

<style scoped>
    .document-list-table {
        min-height: 70vh;
        position: relative;
    }

    .document-list-loading {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fff;
    }

    .document-list-empty {
        min-height: 70vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .table-borderless>:not(caption)>*>*{
        padding: 5px !important
    }
</style>
